import { Box, Popover } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap';
import { ReactNode, useEffect, useRef } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import { RichTextExtraTools, RichTextTools, ToolbarType } from 'shared/types/RichTextToolbar';
import { useUiLanguage } from 'shared/utils/useUiLanguage';

import classes from './RichTextEditorFixed.module.scss';
import { WidgetDefaultStyles } from '../../../utils/typographyStyles';
import { RichTextProps } from '../RichText';
import { RichTextToolbar } from '../RichTextToolbar';

export type RichTextEditorFixedProps = {
  tools?: (RichTextTools | RichTextExtraTools)[];
  withToolbar?: boolean;
  customControls?: ReactNode;
  widgetDefaultStyles?: WidgetDefaultStyles;
};

export function RichTextEditorFixed(props: RichTextProps & RichTextEditorFixedProps) {
  const { tools, customControls, widgetDefaultStyles } = props;
  const { uiDirection } = useUiLanguage();

  const { editor } = useRichTextEditorContext();

  const editorContentRef = useRef<HTMLDivElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);

  const [isToolbarOpened, { close, open }] = useDisclosure();

  useOnClickOutside(
    [editorContentRef as React.RefObject<HTMLElement>, toolbarRef as React.RefObject<HTMLElement>],
    close
  );

  useEffect(() => {
    editor?.on('focus', open);
    return () => {
      editor?.off('focus', open);
    };
  }, [editor, open]);

  return (
    <Popover
      withinPortal
      portalProps={{ target: '#rich-text-editor-portal' }}
      opened={isToolbarOpened}
      classNames={{ dropdown: classes[`toolbarPopover--${uiDirection}`] }}
      shadow="md"
    >
      <Popover.Dropdown id="text-editor-toolbar" data-testid="text-editor-toolbar">
        <RichTextEditor.Toolbar ref={toolbarRef}>
          <RichTextToolbar
            editor={editor}
            type={ToolbarType.Fixed}
            tools={tools}
            widgetDefaultStyles={widgetDefaultStyles}
          >
            {customControls}
          </RichTextToolbar>
        </RichTextEditor.Toolbar>
      </Popover.Dropdown>
      <Popover.Target>
        <Box className={classes.content}>
          <RichTextEditor.Content ref={editorContentRef} />
        </Box>
      </Popover.Target>
    </Popover>
  );
}
